import PropTypes from 'prop-types'

import withSuspense from 'utils/withSuspense'

import { useCurrentWebsiteID } from 'components/CurrentWebsiteIDProvider'

import usePageViewedEvent from 'hooks/usePageViewedEvent'


// Renders a page and reports the view under the name the route gives it.
//
// The name is declared here rather than alongside the page's translated title:
// analytics groups its reports by this value, so it stays in English and stays
// put when the page's user-facing copy changes.
//
// Most routes that render a page without this one report nothing: the pages
// reachable before signing in, the redirects, and the 404s. The exception is a
// page that reports its own view, which it does when the event carries something
// only that page knows, or when the page may send the visitor away instead of
// showing itself.
//
// N.B.: Nothing enforces that distinction. A route added later without this
// component reports nothing and no test fails, so adding a page means naming it
// here as well. A guard test could walk the route tree and require every page
// element to be either this component or an explicit opt-out, which was left
// undone because it is brittle against how the routes nest.
//
export default function TrackedPage({ component, trackingName }) {
  // Undefined for the pages that do not belong to a website, since those have no
  // provider above them.
  const websiteID = useCurrentWebsiteID()

  // The view is reported on navigation, not once the page has finished
  // rendering, and reporting on render is not achievable here anyway: a page
  // whose code fails to download renders a failure placeholder instead, which is
  // an ordinary successful render as far as anything here can tell. Reporting
  // below the suspense boundary would report those views too, only later.
  //
  // Navigation is also what a URL-based pageview counted before this event
  // existed, so a failed download inflating the count is the behavior we already
  // had. Those failures report themselves separately, which is what to reconcile
  // against if the numbers ever look high.
  usePageViewedEvent({
    trackingName,
    websiteID,
  })

  return withSuspense(component)
}

TrackedPage.propTypes = {
  component: PropTypes.elementType.isRequired,
  trackingName: PropTypes.string.isRequired,
}
